import { Chat, Help as HelpIcon } from '@tunasong/icons'
import { Dialog, ListItemIcon, ListItemText, MenuItem, ShortcutBox, shortcuts } from '@tunasong/ui-lib'
import { isSupportChatAvailable, isSupportChatEnabled, openSupportChat } from '@tunasong/vendor'
import type { FC } from 'react'
import { useId } from 'react'
import styles from './help-menu.module.css'

export interface KeyboardShortcutsMenuItemProps {
  /** Show the shortcuts dialog. The menu's owner renders it: anything inside the menu unmounts when it closes. */
  onShow(): void
}

export const KeyboardShortcutsMenuItem: FC<KeyboardShortcutsMenuItemProps> = ({ onShow }) => (
  <MenuItem onClick={onShow}>
    <ListItemIcon>
      <HelpIcon />
    </ListItemIcon>
    <ListItemText primary="Keyboard shortcuts" />
  </MenuItem>
)

const unavailableReason = 'Support chat is unavailable — it may be blocked by your browser.'

/**
 * Opens the Crisp support chat (the floating bubble is hidden on entity pages). Not rendered when
 * support chat is off for this build. When Crisp has not booted (e.g. blocked), the item stays
 * listed, disabled, with the reason as visible secondary text and its accessible description.
 */
export const ContactSupportMenuItem: FC = () => {
  const labelId = useId()
  const reasonId = useId()
  if (!isSupportChatEnabled()) {
    return null
  }
  const disabled = !isSupportChatAvailable()

  return (
    <MenuItem
      className={styles.item}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      aria-labelledby={labelId}
      aria-describedby={disabled ? reasonId : undefined}
      onClick={() => openSupportChat()}
    >
      <ListItemIcon className={styles.icon}>
        <Chat />
      </ListItemIcon>
      <ListItemText
        className={styles.text}
        primary={
          <span id={labelId} className={styles.label}>
            Contact support
          </span>
        }
        secondary={
          disabled ? (
            <span id={reasonId} className={styles.reason}>
              {unavailableReason}
            </span>
          ) : undefined
        }
      />
    </MenuItem>
  )
}

export interface KeyboardShortcutsDialogProps {
  open: boolean
  onClose(): void
}

export const KeyboardShortcutsDialog: FC<KeyboardShortcutsDialogProps> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} title="Keyboard Shortcuts">
    {Object.values(shortcuts).map((s, idx) => (
      <ShortcutBox key={idx} shortcut={s} />
    ))}
  </Dialog>
)
